// src/hooks/useSensorApi.tsx

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  AreaInSqm,
  FolderId,
  FolderPool,
  FolderSingle,
  NumberOfEmployees,
} from '@/types/domain/folder'
import {
  DataSamplingDate,
  DeviceId,
  MeterNumber,
  MeterUnit,
  SamplingMode,
  SensorId,
  SensorName,
  SensorPool,
  SensorProvider,
  SensorRecord,
  SensorStatus,
  SensorTarget,
  SensorType,
  SubSensorTarget,
  TypeInFolder,
} from '@/types/domain/sensor'

import {
  authHeader,
  categoryByType,
  FILTER_TYPE_CONTRACT,
  FILTER_TYPE_FOLDER,
  FILTER_TYPE_INSTALLATION,
  FILTER_TYPE_PERIOD,
  FILTER_TYPE_USER,
  notifyError,
  notifySuccess,
  sanitizeJSON,
} from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC, svgConstants as SVG } from '../constants/'

import { refreshToken } from './auth/'
import { useFolder, useFolderPool } from './useFolderApi'

const { REACT_APP_API_URL } = import.meta.env

type SensorSingle = SensorRecord & {
  contracts: any[]
  folders: any[]
  periods: any[]
  installation: any[]
  users: number[]
}

// context

type SensorContextValue = {
  data: SensorPool
  byId: Record<string, SensorSingle>
  setData: Dispatch<SetStateAction<SensorPool>>
  refresh: (_opts?: { force?: boolean }) => Promise<SensorPool>
  getById: (
    _id: number,
    _opts?: { force?: boolean }
  ) => Promise<SensorSingle | null>
}

const SensorCtx = createContext<SensorContextValue>({} as SensorContextValue)

type SensorDataProviderProps = {
  children: ReactNode
}

export const SensorDataProvider = ({ children }: SensorDataProviderProps) => {
  const { start, stop } = usePreloader()

  const [sensors, setSensors] = useState<SensorPool>([])
  const [byId, setById] = useState<Record<string, SensorSingle>>({})

  const sensorsReqRef = useRef<Promise<SensorPool> | null>(null)

  const sensorReqsRef = useRef<
    Record<string, Promise<SensorSingle | null> | null>
  >({})

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      await refreshToken()
      return await fn()
    },
    []
  )

  const withPreloader = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const fetchSensors = useCallback(
    async ({
      force = false,
    }: { force?: boolean } = {}): Promise<SensorPool> => {
      if (sensorsReqRef.current && !force) return sensorsReqRef.current

      sensorsReqRef.current = (async () => {
        try {
          const payload = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/sensors/admin/sensors`
              const options = { headers: { ...authHeader() }, method: 'GET' }

              const res = await fetch(url, options)
              if (!res.ok) {
                const text = await res.text().catch(() => '')
                throw new Error(text || `GET sensors ${res.status}`)
              }

              // Erste Ebene: API-Antwort (kann leer sein und nur den S3-Link tragen)
              const raw = await res.text()
              const safe = sanitizeJSON(
                typeof raw === 'string' ? raw : JSON.stringify(raw)
              )
              const json = JSON.parse(safe)

              const offloaded =
                json?.meta?.offloaded_to_s3 === true &&
                typeof json?.links?.download === 'string' &&
                json.links.download.length > 0

              if (!offloaded) {
                // Klassischer Pfad: data direkt in der Antwort
                const data = Array.isArray(json?.data) ? json.data : []
                return data
              }

              // Offloaded: zweite Fetch-Runde gegen den präsignierten S3-Link
              const s3Url = json.links.download
              const s3Res = await fetch(s3Url, { method: 'GET' }) // presigned -> keine Auth-Header nötig
              if (!s3Res.ok) {
                const text = await s3Res.text().catch(() => '')
                throw new Error(text || `S3 download ${s3Res.status}`)
              }

              // Große Dateien: als Text lesen, sanitizen, dann parsen
              const s3Text = await s3Res.text()
              const s3Safe = sanitizeJSON(s3Text)
              const s3Json = JSON.parse(s3Safe)

              // Der S3-Inhalt kann entweder ein Array sein oder ein JSON:API-Objekt
              if (Array.isArray(s3Json)) return s3Json
              if (Array.isArray(s3Json?.data)) return s3Json.data

              // Fallback: nichts Verwertbares gefunden
              return []
            })
          )

          if (Array.isArray(payload)) setSensors(payload)
          return Array.isArray(payload) ? payload : []
        } catch (error) {
          const message = JSON.parse(error?.message)?.message
          notifyError(`SensorApi: GET sensors ${message}`)
          return []
        } finally {
          sensorsReqRef.current = null
        }
      })()

      return sensorsReqRef.current
    },
    [withRequest, withPreloader]
  )

  const fetchSensorById = useCallback(
    async (
      id: SensorId,
      { force = false }: { force?: boolean } = {}
    ): Promise<SensorSingle | null> => {
      if (!id || id <= 0) return null
      const key = String(id)
      if (sensorReqsRef.current[key] && !force)
        return sensorReqsRef.current[key]

      sensorReqsRef.current[key] = (async () => {
        const includes = [
          'contracts',
          'folders',
          'periods',
          'installations',
          'users',
        ]
        let request = String(id)
        if (includes.length > 0) {
          request += '?include='
          includes.forEach(val => {
            request += encodeURIComponent(val)
            request += ','
          })
        }

        const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${request}`
        const options = { headers: { ...authHeader() }, method: 'GET' }

        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const response = await fetch(url, options)
              if (!response.ok) {
                const text = await response.text().catch(() => '')
                throw new Error(text || `HTTP ${response.status}`)
              }

              const payload = await response.json()
              const payloadData = payload?.data
              const included = payload?.included
              const attributes = payloadData?.attributes || {}

              const result = {
                ...attributes,
                contracts: included
                  ? included
                      .filter(FILTER_TYPE_CONTRACT)
                      .map(contract => contract.attributes)
                  : [],
                folders: included
                  ? included.filter(FILTER_TYPE_FOLDER).map(f => f.attributes)
                  : [],
                periods: included
                  ? included.filter(FILTER_TYPE_PERIOD).map(period => ({
                      period_start: period.period.period_start,
                      period_end: period.period.period_end,
                      sensor_id: period.period.sensor_id,
                      factor: period.period.factor,
                    }))
                  : [],
                installation: included
                  ? included
                      .filter(FILTER_TYPE_INSTALLATION)
                      .map(installation => ({
                        entity_id:
                          installation.installation.attributes.installation_id,
                      }))
                  : [],
                users: included
                  ? included
                      .filter(FILTER_TYPE_USER)
                      .map(user => Number(user.id))
                  : [],
              }

              setById(prev => ({ ...prev, [key]: result }))
              return result
            })
          )

          return data || null
        } finally {
          sensorReqsRef.current[key] = null
        }
      })()

      return sensorReqsRef.current[key]
    },
    [withRequest, withPreloader]
  )

  const didInitRef = useRef(false)

  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true
    fetchSensors()
  }, [fetchSensors])

  const sensorsValue = useMemo(
    () => ({
      data: sensors,
      byId: byId,
      setData: setSensors,
      refresh: fetchSensors,
      getById: fetchSensorById,
    }),
    [sensors, byId, fetchSensors, fetchSensorById]
  )

  return (
    <SensorCtx.Provider value={sensorsValue}>{children}</SensorCtx.Provider>
  )
}

// pool

export const useSensorPool = (): SensorPool => {
  const ctx = useContext(SensorCtx)
  if (!ctx) return []
  if (!ctx.data) return []

  return ctx.data
}

// api

export const useSensorApi = () => {
  const { refresh: refreshSensors } = useContext(SensorCtx)!

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      await refreshToken()
      return await fn()
    },
    []
  )

  type SensorFormData = Partial<SensorRecord> & {
    contract_id?: (string | number)[]
    time_periods?: any[]
    installation?: any[]
    custom_fields?: any[]
    successor_id?: SensorId

    area_in_sqm?: AreaInSqm
    attributes?: any
    co2_factor?: number | null
    converter_factor?: number | null
    factor?: number | null
    fuel_value?: number | null
    number_of_employees?: NumberOfEmployees
    state_number?: number | null
    lifecycle_status?: number
    previous_lifecycle_status?: number
  }

  const recordData = useCallback(
    (
      data: SensorFormData
    ): {
      data: {
        attributes: Record<string, unknown>
        relationships: Record<string, unknown>
      }
    } => {
      const {
        alert_after_months,
        area_in_sqm,
        attributes,
        battery_comment,
        battery_date,
        battery_type,
        co2_factor,
        contract_id,
        converter_factor,
        correction_value,
        counter_reading,
        counter_reading_date,
        custom_fields,
        data_sampling_date,
        factor,
        folder_id,
        fuel_value,
        installation,
        is_active,
        meter_number,
        meter_unit,
        name,
        number_of_employees,
        lifecycle_status,
        previous_lifecycle_status,
        parent_id,
        push_enabled,
        sampling_mode,
        sensor_model,
        sensor_target,
        state_number,
        sub_sensor_target,
        subcategory,
        successor_id,
        time_periods,
        type_in_folder,
        unit,
        outdoor_sensor,
        indoor_sensor,
        correction_type,
        value_mode,
        warning_after_months,
      } = data

      return {
        data: {
          attributes: {
            alert_after_months,
            area_in_sqm,
            attributes,
            battery_comment,
            battery_date,
            battery_type,
            counter_reading,
            counter_reading_date,
            custom_fields,
            data_sampling_date,
            factor: factor ? factor : 1,
            fuel_value: fuel_value ? fuel_value : 1,
            is_active: is_active ? 1 : 0,
            name,
            number_of_employees,
            parent_id: parent_id ? parent_id : null,
            sensor_model,
            sensor_target,
            state_number: state_number ? state_number : 1,
            converter_factor: converter_factor ? converter_factor : 1,
            subcategory: subcategory ? subcategory : null,
            sub_sensor_target,
            type_in_folder: type_in_folder ? type_in_folder : null,
            co2_factor: co2_factor ? co2_factor : 1,
            unit: unit ? unit : null,
            sampling_mode: sampling_mode ? sampling_mode : null,
            meter_number: meter_number ? meter_number : null,
            meter_unit: meter_unit ? meter_unit : null,
            correction_value: correction_value !== 0 ? correction_value : null,
            lifecycle_status: lifecycle_status ? lifecycle_status : 0,
            previous_lifecycle_status: previous_lifecycle_status
              ? previous_lifecycle_status
              : 0,
            push_enabled:
              typeof push_enabled === 'boolean' ? push_enabled : null,
            value_mode,
            warning_after_months,
          },
          relationships: {
            contracts: {
              data: (contract_id ?? []).map(item => ({
                type: 'contract',
                id: item,
              })),
            },
            factor_periods: time_periods,
            folders: {
              data: folder_id ? [{ type: 'folder', id: folder_id }] : [],
            },
            installations: installation,
            sensors_order: {
              data: { successor_id: successor_id ? successor_id : null },
            },
            sensors_temperature_correction: {
              data: {
                outdoor_sensors: outdoor_sensor ? outdoor_sensor : [],
                indoor_sensors: indoor_sensor ? indoor_sensor : [],
                correction_type: correction_type === 0 ? null : correction_type,
              },
            },
            sensors_relation: {
              data: { parent_id: parent_id ? parent_id : null },
            },
          },
        },
      }
    },
    []
  )

  const save = useCallback(
    async (data: SensorFormData) =>
      withRequest(async () => {
        const name = data?.name
        const deviceIds = Array.isArray(data?.device_id)
          ? data.device_id
          : data?.device_id
            ? [data.device_id]
            : []

        if (!name || deviceIds.length === 0) {
          const message = LC.SENSOR_DEVICE_REQUIRED
          notifyError(message)
          return null
        }

        const results = []
        const errors = []

        await Promise.all(
          deviceIds.map(async item => {
            const deviceId = item?.value ?? item
            const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${deviceId}`
            const options = {
              method: 'PATCH',
              headers: { ...authHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify(recordData(data)),
            }

            try {
              const response = await fetch(url, options)

              if (!response.ok) {
                const text = await response.text().catch(() => '')
                throw new Error(text || `HTTP ${response.status}`)
              }

              const result = await response.json()
              results.push(result)
              notifySuccess(LC.SENSOR_SAVED)
            } catch (err: any) {
              const message = err instanceof Error ? err.message : String(err)
              errors.push(message)
              notifyError(message)
            }
          })
        )

        if (errors.length > 0) notifyError(errors.join(' | '))

        await refreshSensors({ force: true })
        return results
      }),
    [recordData, refreshSensors, withRequest]
  )

  const update = useCallback(
    async (data: SensorFormData & { sensorId?: SensorId }) =>
      withRequest(async () => {
        const sId: SensorId = data?.sensorId
        if (!sId) {
          const message = LC.SENSOR_DEVICE_REQUIRED
          notifyError(message)
          return null
        }

        const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${sId}`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(recordData(data)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }

        const result = await response.json()
        await refreshSensors({ force: true })
        notifySuccess(LC.SENSOR_UPDATED)
        return result
      }),
    [recordData, refreshSensors, withRequest]
  )

  const remove = useCallback(
    async (id: SensorId) =>
      withRequest(async () => {
        if (!id) {
          const message = LC.SENSOR_DEVICE_REQUIRED
          notifyError(message)
          return false
        }

        const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'DELETE',
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw new Error(text || `HTTP ${response.status}`)
        }

        notifySuccess(LC.SENSOR_DELETED)

        await refreshSensors({ force: true })
        return true
      }),
    [refreshSensors, withRequest]
  )

  return { save, update, remove, refreshSensors }
}

export const useSensor = (id: SensorId): SensorSingle | null => {
  const { byId, getById } = useContext(SensorCtx)!
  const key = String(id || '')
  const sensor = key ? byId[key] || null : null

  useEffect(() => {
    if (!id) return
    if (!sensor) getById(id)
  }, [id, sensor, getById])

  return sensor
}

export const useConsumptionSensors = (id: FolderId): SensorRecord[] => {
  const [consumptionSensors, setConsumptionSensors] = useState<SensorRecord[]>(
    []
  )
  const fData: FolderSingle = useFolder(id)

  useEffect(() => {
    if (!id || !fData?.sensors) return

    const FILTER_CONSUMPTION = (f: SensorRecord) =>
      f.sensor_target === 'consumption' ||
      f.sensor_target === 'performance' ||
      categoryByType(f.sensor_type, f.type_in_folder) === 'consumption' ||
      categoryByType(f.sensor_type, f.type_in_folder) === 'performance'

    setConsumptionSensors(
      fData.sensors.filter(FILTER_CONSUMPTION) as SensorRecord[]
    )
  }, [fData, id])

  return consumptionSensors
}

export const useDataSamplingDate = (id: SensorId): DataSamplingDate => {
  const { data = [] } = useContext(SensorCtx)!
  const [dataSamplingDate, setDataSamplingDate] = useState<DataSamplingDate>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const d: DataSamplingDate = record?.attributes?.data_sampling_date
    if (!d) return

    setDataSamplingDate(d)
  }, [data, id])

  return dataSamplingDate
}

export const useDeviceId = (id: SensorId): DeviceId => {
  const { data = [] } = useContext(SensorCtx)!
  const [deviceId, setDeviceId] = useState<DeviceId>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const d: DeviceId = record?.attributes?.device_id
    if (!d) return

    setDeviceId(d)
  }, [data, id])

  return deviceId
}

export const useFeelingSensors = (id: FolderId): SensorRecord[] => {
  const fPool: FolderPool = useFolderPool()
  const { data = [] } = useContext(SensorCtx)!
  const [feelingSensors, setFeelingSensors] = useState<SensorRecord[]>([])

  useEffect(() => {
    if (!id || fPool.length === 0) return

    const FILTER_FEELING = (f: SensorRecord) =>
      (categoryByType(f.sensor_type) === 'feeling' &&
        f.sensor_target !== 'security') ||
      f.sensor_target === 'feeling'

    const sIds: SensorId[] = fPool
      .find(f => f.attributes.entity_id === id)
      .attributes.folder_sensors.map(f => f.sensor_id)
      .sort()

    const f: SensorRecord[] = data
      .map(s => s.attributes)
      .filter(s => sIds.includes(s.entity_id))
      .filter(FILTER_FEELING)

    setFeelingSensors(f)
  }, [id, fPool, data])

  return feelingSensors
}

export const useMeterNumber = (id: SensorId): MeterNumber => {
  const { data = [] } = useContext(SensorCtx)!
  const [meterNumber, setMeterNumber] = useState<MeterNumber>('n/a')

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const n: MeterNumber = record?.attributes?.meter_number
    if (!n) return

    setMeterNumber(n)
  }, [data, id])

  return meterNumber
}

export const useMeterUnit = (id: SensorId): MeterUnit => {
  const { data = [] } = useContext(SensorCtx)!
  const [meterUnit, setMeterUnit] = useState<MeterUnit>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const u: MeterUnit = record?.attributes?.meter_unit
    if (!u) return

    setMeterUnit(u)
  }, [data, id])

  return meterUnit
}

export const useParentId = (id: SensorId): SensorId | null => {
  const { data = [] } = useContext(SensorCtx)!
  const [parentId, setParentId] = useState<SensorId>(null)

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const s: SensorId = record?.attributes?.parent_id
    if (!s) return

    setParentId(s)
  }, [data, id])

  return parentId
}

export const useSamplingMode = (id: SensorId): SamplingMode => {
  const { data = [] } = useContext(SensorCtx)!
  const [samplingMode, setSamplingMode] = useState<SamplingMode>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const s: SamplingMode = record?.attributes?.sampling_mode
    if (!s) return

    setSamplingMode(s)
  }, [data, id])

  return samplingMode
}

export const useSecuritySensors = (id: FolderId): SensorRecord[] => {
  const fPool: FolderPool = useFolderPool()
  const { data = [] } = useContext(SensorCtx)!
  const [securitySensors, setSecuritySensors] = useState<SensorRecord[]>([])

  useEffect(() => {
    if (!id || fPool.length === 0) return

    const FILTER_SECURITY = (f: SensorRecord) =>
      (categoryByType(f.sensor_type) === 'security' &&
        f.sensor_target !== 'feeling') ||
      f.sensor_target === 'security'

    const sIds: SensorId[] = fPool
      .find(f => f.attributes.entity_id === id)
      .attributes.folder_sensors.map(f => f.sensor_id)
      .sort()

    const s: SensorRecord[] = data
      .map(s => s.attributes)
      .filter(s => sIds.includes(s.entity_id))
      .filter(FILTER_SECURITY)

    setSecuritySensors(s)
  }, [id, fPool, data])

  return securitySensors
}

export const useSensorIcon = (
  id: SensorId,
  status = 'neutral',
  isOpen?: boolean
) => {
  const { data = [] } = useContext(SensorCtx)!
  const [icon, setIcon] = useState('')

  useEffect(() => {
    if (!id) return

    const result = data.find(s => Number(s.id) === id)
    const type: SensorType = result?.attributes?.sensor_type
    const sst: SubSensorTarget = result?.attributes?.sub_sensor_target

    if (!type) return

    const getSensorIcon = () => {
      switch (type) {
        case 'energy':
        case 'energy_detail':
        case 'gas':
        case 'gas_detail':
        case 'water':
        case 'water_detail':
          switch (status) {
            case 'green':
              return SVG.CONSUMPTION_GOOD
            case 'yellow':
              return SVG.CONSUMPTION_WARNING
            case 'red':
              return SVG.CONSUMPTION_ALERT
            default:
              return SVG.CONSUMPTION_INACTIVE
          }

        case 'co2':
          switch (status) {
            case 'green':
              return SVG.CO2_GOOD
            case 'yellow':
              return SVG.CO2_WARNING
            case 'red':
              return SVG.CO2_ALERT
            default:
              return SVG.CO2_INACTIVE
          }

        case 'humidity':
          switch (status) {
            case 'green':
              return SVG.HUMIDITY_GOOD
            case 'yellow':
              return SVG.HUMIDITY_WARNING
            case 'red':
              return SVG.HUMIDITY_ALERT
            default:
              return SVG.HUMIDITY_INACTIVE
          }

        case 'light':
          switch (status) {
            case 'green':
              return SVG.LIGHT_GOOD
            case 'yellow':
              return SVG.LIGHT_WARNING
            case 'red':
              return SVG.LIGHT_ALERT
            default:
              return SVG.LIGHT_INACTIVE
          }

        case 'temperature':
          switch (status) {
            case 'green':
              return SVG.TEMPERATURE_GOOD
            case 'yellow':
              return SVG.TEMPERATURE_WARNING
            case 'red':
              return SVG.TEMPERATURE_ALERT
            default:
              return SVG.TEMPERATURE_INACTIVE
          }

        case 'external_temp_1':
        case 'pressure':
        case 'temperature_0':
        case 'temperature_1':
        case 'temperature_2':
        case 'Ni1000':
        case 'degrees-celsius':
          switch (status) {
            case 'green':
              return SVG.TEMPERATURE_GOOD
            case 'yellow':
              return SVG.TEMPERATURE_WARNING
            case 'red':
              return SVG.TEMPERATURE_ALERT
            default:
              return SVG.TEMPERATURE_INACTIVE
          }

        case 'tor1CurrentState':
        case 'tor1PreviousState':
          switch (status) {
            case 'green':
              return SVG.DOOR_LOCKED_GOOD
            case 'yellow':
              return SVG.DOOR_LOCKED_WARNING
            case 'red':
              return SVG.DOOR_LOCKED_ALERT
            default:
              return SVG.DOOR_LOCKED_INACTIVE
          }

        case 'door':
        case 'door_channel1':
        case 'door_channel2':
        case 'door_channel3':
        case 'door_channel4':
        case 'tor2CurrentState':
        case 'tor2PreviousState':
          switch (status) {
            case 'green':
              return isOpen ? SVG.DOOR_OPEN_GOOD : SVG.DOOR_CLOSED_GOOD
            case 'yellow':
              return isOpen ? SVG.DOOR_OPEN_WARNING : SVG.DOOR_CLOSED_WARNING
            case 'red':
              return isOpen ? SVG.DOOR_OPEN_ALERT : SVG.DOOR_CLOSED_ALERT
            default:
              return isOpen ? SVG.DOOR_OPEN_INACTIVE : SVG.DOOR_CLOSED_INACTIVE
          }

        case 'waterDetected':
          switch (status) {
            case 'green':
              return SVG.WATER_DETECTOR_GOOD
            case 'yellow':
              return SVG.WATER_DETECTOR_WARNING
            case 'red':
              return SVG.WATER_DETECTOR_ALERT
            default:
              return SVG.WATER_DETECTOR_INACTIVE
          }

        case 'elevator':
          switch (status) {
            case 'green':
              return SVG.ELEVATOR_GOOD
            case 'red':
              return SVG.ELEVATOR_ALERT
            default:
              return SVG.ELEVATOR_INACTIVE
          }

        case 'external_digital_button':
          if (sst === 'elevator') {
            switch (status) {
              case 'green':
                return SVG.ELEVATOR_GOOD
              case 'red':
                return SVG.ELEVATOR_ALERT
              default:
                return SVG.ELEVATOR_INACTIVE
            }
          }
          if (sst === 'lifting') {
            switch (status) {
              case 'green':
                return SVG.LIFTING_STATION_GOOD
              case 'red':
                return SVG.LIFTING_STATION_ALERT
              case 'yellow':
                return SVG.LIFTING_STATION_WARNING
              default:
                return SVG.LIFTING_STATION_INACTIVE
            }
          }
          return SVG.UNKNOWN_SENSOR
        case 'lifting':
          switch (status) {
            case 'green':
              return SVG.LIFTING_STATION_GOOD
            case 'red':
              return SVG.LIFTING_STATION_ALERT
            case 'yellow':
              return SVG.LIFTING_STATION_WARNING
            default:
              return SVG.LIFTING_STATION_INACTIVE
          }

        case 'smokeDetektor':
        case 'Smoke_alarm':
        case 'Battery_end_of_life':
        case 'Removal_detection':
        case 'Acoustic_alarm_failure':
          switch (status) {
            case 'green':
              return SVG.SMOKE_DETECTOR_GOOD
            case 'red':
              return SVG.SMOKE_DETECTOR_ALERT
            case 'yellow':
              return SVG.SMOKE_DETECTOR_WARNING
            default:
              return SVG.SMOKE_DETECTOR_INACTIVE
          }

        default:
          console.error('unhandled sensor type', type)
          return SVG.UNKNOWN_SENSOR
      }
    }

    setIcon(getSensorIcon())
  }, [data, id, isOpen, status])

  return icon
}

export const useSensorName = (id: SensorId): SensorName => {
  const { data = [] } = useContext(SensorCtx)!
  const [name, setName] = useState<SensorName>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const n: SensorName = record?.attributes?.name
    if (!n) return

    setName(n)
  }, [data, id])

  return name
}

export const useSensorPoolIds = (): SensorId[] => {
  const { data = [] } = useContext(SensorCtx)!
  const [poolIds, setPoolIds] = useState<SensorId[]>([])

  useEffect(() => {
    const ids = data.map(s => s.attributes.entity_id).sort((a, b) => a - b)

    setPoolIds(ids)
  }, [data])

  return poolIds
}

export const useSensorProvider = (id: SensorId): SensorProvider => {
  const { data = [] } = useContext(SensorCtx)!
  const [provider, setProvider] = useState<SensorProvider>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const p: SensorProvider = record?.attributes?.provider
    if (!p) return

    setProvider(p)
  }, [data, id])

  return provider
}

export const useSensorStatus = (id: SensorId): SensorStatus => {
  const { data = [] } = useContext(SensorCtx)!
  const [status, setStatus] = useState<SensorStatus>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const s: SensorStatus = record?.attributes?.status
    if (!s) return

    setStatus(s)
  }, [data, id])

  return status
}

export const useSensorTarget = (id: SensorId): SensorTarget => {
  const { data = [] } = useContext(SensorCtx)!
  const [target, setTarget] = useState<SensorTarget>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const t: SensorTarget = record?.attributes?.sensor_target
    if (!t) return

    setTarget(t)
  }, [data, id])

  return target
}

export const useSensorType = (id: SensorId): SensorType => {
  const { data = [] } = useContext(SensorCtx)!
  const [type, setType] = useState<SensorType>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const t: SensorType = record?.attributes?.sensor_type
    if (!t) return

    setType(t)
  }, [data, id])

  return type
}

export const useSubSensorTarget = (id: SensorId): SubSensorTarget => {
  const { data = [] } = useContext(SensorCtx)!
  const [subTarget, setSubTarget] = useState<SubSensorTarget>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const t: SubSensorTarget = record?.attributes?.sub_sensor_target
    if (!t) return

    setSubTarget(t)
  }, [data, id])

  return subTarget
}

export const useTypeInFolder = (id: SensorId): TypeInFolder => {
  const { data = [] } = useContext(SensorCtx)!
  const [typeInFolder, setTypeInFolder] = useState<TypeInFolder>()

  useEffect(() => {
    if (!id) return

    const record = data.find(f => Number(f.id) === id)
    const tif: TypeInFolder = record?.attributes?.type_in_folder
    if (!tif) return

    setTypeInFolder(tif)
  }, [data, id])

  return typeInFolder
}
